import * as React from "react"
import { hasExactlyN, hasFirstElement } from "@digits-shared/helpers/arrayHelper"
import { useOnScreen } from "@digits-shared/hooks/useOnScreen"
import useScrollContext from "@digits-shared/hooks/useScrollContext"
import styled from "styled-components"

const SafeArea = styled.div`
  position: absolute;
  height: env(safe-area-inset-top);
  width: 0;
  visibility: hidden;
`

export const StickySentinel: React.FC<{
  stickRef: React.RefObject<HTMLElement | null>
  rootMargin?: string
  disabled?: boolean
}> = ({ stickRef, rootMargin, disabled }) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const adjustedRootMargin = useAdjustedRootMargin(ref, rootMargin, disabled)
  const isOnScreen = useOnScreen(ref, { rootMargin: adjustedRootMargin })
  const { scrollElement } = useScrollContext()

  React.useEffect(() => {
    const sticky = stickRef.current
    if (!sticky || disabled) return

    const scrollEl = scrollElement.current
    const viewPortHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue("--viewport-height").trim(),
      10
    )
    const scrollElHeight = scrollEl?.scrollHeight ?? 0

    if (
      scrollElHeight < viewPortHeight * 1.2 ||
      (!isOnScreen && !scrollEl?.scrollTop) ||
      getComputedStyle(sticky).getPropertyValue("position") !== "sticky"
    ) {
      sticky?.classList.remove("stuck")
      return
    }

    sticky.classList.toggle("stuck", !isOnScreen)
    return () => {
      sticky.classList.remove("stuck")
    }
  }, [disabled, isOnScreen, scrollElement, stickRef])

  return <SafeArea ref={ref} data-testid="safe-area" />
}

function useAdjustedRootMargin(
  ref: React.RefObject<HTMLDivElement | null>,
  rootMargin?: string,
  disabled?: boolean
) {
  const [adjustedRootMargin, setAdjustedRootMargin] = React.useState(rootMargin)

  React.useEffect(() => {
    if (disabled) return
    const insetTop = ref.current ? parseFloat(window.getComputedStyle(ref.current).height) * 2 : 0
    // Handle different rootMargin formats
    const margins = rootMargin?.split(" ") ?? []
    if (hasFirstElement(margins)) {
      const currentMargin = parseFloat(margins[0])
      const isNegative = margins[0].startsWith("-")
      const adjustedTopMargin = isNegative
        ? -(Math.abs(currentMargin) + insetTop)
        : currentMargin + insetTop

      if (hasExactlyN(margins, 1)) {
        // Single value format (e.g. "0px")
        setAdjustedRootMargin(`${adjustedTopMargin}px ${currentMargin}px ${currentMargin}px`)
      } else if (hasExactlyN(margins, 2)) {
        // Two value format (e.g. "0px 0px")
        setAdjustedRootMargin(`${adjustedTopMargin}px ${margins[1]} ${currentMargin}px`)
      } else if (hasExactlyN(margins, 3)) {
        // Three value format (e.g. "0px 0px 0px")
        setAdjustedRootMargin(`${adjustedTopMargin}px ${margins[1]} ${margins[2]}`)
      } else if (hasExactlyN(margins, 4)) {
        // Four value format (e.g. "0px 0px 0px 0px")
        setAdjustedRootMargin(`${adjustedTopMargin}px ${margins[1]} ${margins[2]} ${margins[3]}`)
      }
    } else {
      setAdjustedRootMargin(`${insetTop}px 0px 0px`)
    }
  }, [rootMargin, ref, disabled])

  return adjustedRootMargin
}
